import { Content, Server } from '@wajeez-web/core/api-base';
import {
  useContentDispatch,
  useContentSelector,
  CollectionComponetsActions,
  ContentEntityActions,
} from '@wajeez-web/stores/content';
import { useEffect, useRef, useState } from 'react';

import { useProductSelector } from '@wajeez-web/stores/product';
import { ThunkStatus } from '@wajeez-web/stores/base';
import axios from 'axios';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface EmptyParameters {}

export interface BaseComponentProps {
  component?: Server.ComponentWithUniqueIdOutput | null;
  context?: Content.ContentEntityOutput | null;
  isLastSibling?: boolean | undefined | 0;
  pageSize?: number;
  type?: string | null | undefined;
  pageDataId?: string | undefined;
  pageDataContextId?: string | undefined;
  extraProps?: { [key: string]: any };
  widthStyle?: string;
  seeMoreType?: 'categories' | 'curatedlist';
  progress?: number;
}

export function useWithContent(id: string | null | undefined) {
  const contentDispatch = useContentDispatch();
  const content = useContentSelector((state) =>
    state.contentEntity.detailsHistory.find((d) => d.id === id)
  );
  useEffect(() => {
    if (!id) return;
    if (content) return;
    contentDispatch(
      ContentEntityActions.addContentEntityByIdForHistoryAsync(id)
    );
  }, [id, content?.id]);
  return content;
}

export function usePageData<TParameters = EmptyParameters>(
  id: string | null | undefined,
  context: Server.ContentEntityOutput | undefined = undefined
): {
  pageData: Server.CollectionComponentDetailsOutput | null;
  getParameterValue: <TKey extends keyof TParameters>(
    key: TKey
  ) => TParameters[TKey] | null | undefined;
  content: Content.ContentEntityOutput | undefined;
  isLockedContent: boolean;
  contentRequestStatus: ThunkStatus;
} {
  const contentDispatch = useContentDispatch();

  const pageDataFromHistory = useContentSelector((c) =>
    c.collectionComponents.detailsHistory.find((d) => d.id === id)
  );
  const pageData = useContentSelector(
    (state) => state.collectionComponents.details
  );
  const contents = useContentSelector(
    (state) =>
      state.contentEntity.contentEentityBySelectorData[
        id + (context?.id + '')
      ] ?? []
  ).data;
  const contentStatus = useContentSelector(
    (state) =>
      state.contentEntity.contentEentityBySelectorStatus[
        id + (context?.id + '')
      ] ?? 'idle'
  );

  useEffect(() => {
    if (!id) return;
    if (pageDataFromHistory) return;
    contentDispatch(
      CollectionComponetsActions.getCollectionComponentByIdAsync(id)
    );
  }, [id, pageDataFromHistory]);

  useEffect(() => {
    const canceller = axios.CancelToken.source();

    if (!pageData || !pageDataFromHistory) return;
    if (
      (pageData.selectors?.length ?? 0) === 0 ||
      (pageDataFromHistory.selectors?.length ?? 0) === 0
    )
      return;
    contentDispatch(
      ContentEntityActions.getContentEntitiesBySelectorsAsync({
        selectors: {
          selectors:
            (pageDataFromHistory ?? pageData).selectors?.map((d) => ({
              factor: d.factor,
              criteria: d.defaultCriteria,
              entityId: d.defaultEntityId ?? context?.id,
              relationLength: d.defaultRelationLength ?? 1,
              typeId: d.defaultTypeId ?? context?.contentEntityTypeId,
            })) || [],
          sorters:
            (pageDataFromHistory ?? pageData)?.sorters?.map((s) => ({
              factor: s.factor,
              direction: s.direction,
            })) || [],
          pageNumber: 1,
          pageSize: 10,
        },
        uniqueId: (pageDataFromHistory ?? pageData).id + (context?.id + ''),
        cancelToken: canceller.token,
      })
    );

    return () => {
      canceller.cancel();
    };
  }, [pageData, context?.id]);

  const results = {
    pageData: pageDataFromHistory ?? pageData ?? null,
    content: contents?.[0] ?? context,
    contentRequestStatus: contentStatus,
  };

  const { isLockedContent } = useLockedContent(results.content);

  const getParameterValue = <TKey extends keyof TParameters>(key: TKey) => {
    return pageData == undefined
      ? null
      : (processParameterValue<TParameters, TKey>(
          key,
          pageDataFromHistory ?? pageData,
          results.content
        ) as TParameters[TKey]);
  };

  return {
    ...results,
    getParameterValue,
    isLockedContent,
  };
}

export function useComponentData<TParameters = EmptyParameters>(
  component?: Server.ComponentWithUniqueIdOutput | null,
  context?: Content.ContentEntityOutput | null,
  pageNumber?: number,
  pageSize?: number
): {
  componentData?: Server.ComponentWithUniqueIdOutput | null;
  getParameterValue: <TKey extends keyof TParameters>(
    key: TKey
  ) => TParameters[TKey] | null | undefined;
  contents: Content.ContentEntityOutput[];
  meta: { hasNextPage: boolean | undefined; totalCount: number | undefined };
  content: Content.ContentEntityOutput | undefined;
  isLockedContent: boolean;
  contentsRequestStatus: ThunkStatus;
} {
  const contentDispatch = useContentDispatch();
  const contents = useContentSelector(
    (state) =>
      state.contentEntity.contentEentityBySelectorData[
        component?.id + (context?.id ?? '')
      ]
  );
  const contentsStatus = useContentSelector(
    ({ contentEntity }) =>
      contentEntity.contentEentityBySelectorStatus[
        component?.id + (context?.id ?? '')
      ]
  );

  // useEffect(() => {
  //   if (!id) return;
  //   contentDispatch(
  //     CollectionComponetsActions.getCollectionComponentByIdAsync(id)
  //   );
  // }, [id]);
  useEffect(() => {
    const canceller = axios.CancelToken.source();

    if (!component) return;
    if ((component.selectors?.length ?? 0) === 0) return;

    (!contents ||
      (contents &&
        pageNumber &&
        contents?.meta.currentPage &&
        pageNumber > contents?.meta.currentPage)) &&
      contentDispatch(
        ContentEntityActions.getContentEntitiesBySelectorsAsync({
          selectors: {
            selectors:
              component.selectors?.map((d) => ({
                factor: d.factor,
                criteria: d.defaultCriteria,
                entityId: d.defaultEntityId ?? context?.id,
                relationLength: d.defaultRelationLength ?? 1,
                typeId: d.defaultTypeId ?? context?.contentEntityTypeId,
              })) || [],
            sorters:
              component?.sorters?.map((s) => ({
                factor: s.factor,
                direction: s.direction,
              })) || [],
            pageNumber: pageNumber ? pageNumber : 1,
            pageSize: pageSize ? pageSize : 10,
          },
          uniqueId: component?.id + (context?.id ?? ''),
          cancelToken: canceller.token,
        })
      );

    return () => {
      canceller.cancel();
    };
  }, [component, context, contents?.data?.length, pageNumber]);

  const results = {
    componentData: component,
    contents: contents?.data,
    meta: contents?.meta,
    content: contents?.data?.[0] ?? context,
    contentsRequestStatus: contentsStatus,
  };

  const { isLockedContent } = useLockedContent(results.content);

  const getParameterValue = <TKey extends keyof TParameters>(key: TKey) =>
    component == undefined
      ? null
      : (processParameterValue<TParameters, TKey>(
          key,
          component,
          results.content
        ) as TParameters[TKey]);

  return {
    ...results,
    getParameterValue,
    isLockedContent,
  };
}

export function useLockedContent(
  context: Content.ContentEntityOutput | null | undefined
) {
  const [isLockedContent, setIsLockedContent] = useState(false);

  const myPlans = useProductSelector(({ plans }) => plans.myPlans);

  useEffect(() => {
    if (context) {
      if (context.plans?.length) {
        const plan = myPlans.find(
          (myPlan) => myPlan.id && context.plans?.includes(myPlan.id)
        );

        if (plan) {
          setIsLockedContent(false);
        } else {
          setIsLockedContent(true);
        }
      } else {
        setIsLockedContent(false);
      }
    }
  }, [myPlans, context?.id, setIsLockedContent]);

  return { isLockedContent };
}

export function useSkeletonHelper(status: ThunkStatus, noData = false) {
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (status === 'idle' && noData) {
      setIsActive(false);
    } else {
      setIsActive(true);
    }
  }, [status, noData]);

  return { isActive };
}

export function processParameterValue<
  TParameters,
  TKey extends keyof TParameters
>(
  key: TKey,
  component: Server.ComponentWithUniqueIdOutput,
  context?: Content.ContentEntityOutput | null
): TParameters[keyof TParameters] | null {
  if (
    component.parameters === undefined ||
    component.parameters[key as string] === undefined
  )
    return null;
  switch (component.parameters[key as string].fieldType) {
    case Content.ComponentFieldType.MappedField:
      return getMappedFieldValue(
        component.parameters[key as string],
        context
      ) as TParameters[TKey];
    case Content.ComponentFieldType.PredefinedComponent:
      return component.parameters[key as string].value as TParameters[TKey];
    case Content.ComponentFieldType.Primitive:
      return component.parameters[key as string].value as TParameters[TKey];
    case Content.ComponentFieldType.RequiredComponent:
      return component.parameters[key as string].id as TParameters[TKey];
  }
  return null;
}

export function getMappedFieldValue(
  field: Server.ComponentFieldOutput,
  context?: Content.ContentEntityOutput | null
) {
  if (context === undefined) return null;
  const fieldName = field.mappingOptions?.find(
    (f) => f.contentEntityTypeId === context?.contentEntityTypeId
  )?.entityTypeField;
  if (fieldName === undefined) return null;
  return context?.parameters?.find((g) => g.key === fieldName)?.value ?? null;
}
